import React, { useState, useEffect } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimesCircle } from 'react-icons/fa';

/**
 * Toast notification component for displaying success, error, warning, or info messages
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the toast is visible
 * @param {function} props.onClose - Function to call when the toast is closed
 * @param {string} props.message - Toast message content
 * @param {string} props.variant - Toast variant (success, danger, warning, info)
 * @param {number} props.delay - Auto-hide delay in milliseconds (default: 3000)
 * @param {string} props.position - Toast position (top-start, top-center, top-end, bottom-start, bottom-center, bottom-end)
 */
const ToastNotification = ({ 
  show, 
  onClose, 
  message, 
  variant = 'success', 
  delay = 3000,
  position = 'top-end'
}) => {
  const [icon, setIcon] = useState(null);
  const [bgClass, setBgClass] = useState('');
  
  // Set icon and background class based on variant
  useEffect(() => {
    switch (variant) {
      case 'success':
        setIcon(<FaCheckCircle />);
        setBgClass('bg-success text-white');
        break;
      case 'danger':
      case 'error':
        setIcon(<FaTimesCircle />);
        setBgClass('bg-danger text-white');
        break;
      case 'warning':
        setIcon(<FaExclamationTriangle />);
        setBgClass('bg-warning text-dark');
        break;
      case 'info':
        setIcon(<FaInfoCircle />);
        setBgClass('bg-info text-white');
        break;
      default:
        setIcon(<FaInfoCircle />);
        setBgClass('bg-success text-white');
    }
  }, [variant]);

  // Parse position for ToastContainer
  const getPosition = () => {
    const [vertical, horizontal] = position.split('-');
    return { position: `${vertical}-${horizontal}` };
  };

  return (
    <ToastContainer {...getPosition()} className="p-3">
      <Toast 
        show={show} 
        onClose={onClose} 
        delay={delay} 
        autohide
        animation={true}
        className={`${bgClass}`}
      >
        <Toast.Header className={`${bgClass} text-white`} closeButton>
          <span className="me-2">{icon}</span>
          <strong className="me-auto">Notification</strong>
        </Toast.Header>
        <Toast.Body className={variant === 'warning' ? 'text-dark' : 'text-white'}>
          {message}
        </Toast.Body>
      </Toast>
    </ToastContainer>
  );
};

export default ToastNotification;
